import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  ArrowLeft, Users, Clock, Loader2, X, Heart, Eye,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  getPacketDetail,
  removeFromPacket,
  type PacketDetail,
  type PacketCandidate,
  type PacketCandidateActionUser,
} from '../../services/api';
import { formatDate } from './constants';

/* ─── Helpers ─── */

function candidateStatusBadge(
  status: string,
  actionAt: string | null,
  actionUsers: PacketCandidateActionUser[] = [],
) {
  const pillFor = (
    actionType: 'Saved' | 'Viewed' | 'Discarded',
    content: React.ReactNode,
    key?: string | number,
  ) => {
    if (actionType === 'Saved') return <span key={key} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium border border-red-100 bg-red-50 text-red-500 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400"><Heart className="w-3 h-3 fill-current" />{content}</span>;
    if (actionType === 'Discarded') return <span key={key} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium border border-gray-300 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"><X className="w-3 h-3" />{content}</span>;
    return <span key={key} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium border border-blue-100 bg-blue-50 text-blue-500 dark:border-blue-900 dark:bg-blue-900/20 dark:text-blue-400"><Eye className="w-3 h-3" />{content}</span>;
  };

  const users = actionUsers.filter((u) => u.firstName);
  if (users.length === 0) {
    if (status === 'Saved' || status === 'Discarded' || status === 'Viewed') {
      return pillFor(status, <>{status}{actionAt ? <> on {formatDate(actionAt)}</> : null}</>);
    }
    return <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium border border-gray-300 bg-transparent text-gray-400 dark:border-gray-700">Not viewed</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {users.map((u, i) =>
        pillFor(
          u.actionType,
          <>{u.actionType} by <span className="font-semibold">{u.firstName}</span> on {formatDate(u.at)}</>,
          `${u.firstName}-${u.at}-${i}`,
        ),
      )}
    </span>
  );
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    + ', '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
}

const ACTIVITY_PRIORITY: Record<string, number> = { Saved: 3, Discarded: 2, Viewed: 1, 'Not viewed': 0 };

const HISTORY_ICON: Record<string, { icon: React.ElementType; bg: string; text: string; border: string; fill?: boolean }> = {
  Saved: { icon: Heart, bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', border: 'border-red-100 dark:border-red-800', fill: true },
  Viewed: { icon: Eye, bg: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' },
  Discarded: { icon: X, bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-100 dark:border-orange-800' },
};

/* ─── Sort Header ─── */

function SortHeader({ label, sortKey, currentSort, onSort }: {
  label: string; sortKey: string;
  currentSort: { col: string; dir: 'asc' | 'desc' } | null;
  onSort: (col: string) => void;
}) {
  const isActive = currentSort?.col === sortKey;
  return (
    <th
      className="px-3 py-3 font-semibold cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        <div className="flex flex-col -space-y-1">
          <ChevronUp className={`w-2.5 h-2.5 ${isActive && currentSort?.dir === 'asc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
          <ChevronDown className={`w-2.5 h-2.5 ${isActive && currentSort?.dir === 'desc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
        </div>
      </div>
    </th>
  );
}

/* ─── Remove Confirmation Modal ─── */

function RemoveConfirmModal({ names, onClose, onConfirm, removing }: {
  names: string[]; onClose: () => void; onConfirm: () => void; removing: boolean;
}) {
  const label = names.length === 1 ? names[0] : `${names.length} candidates`;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200" onClick={(ev) => ev.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Remove from Batch</h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to remove <span className="font-medium text-gray-900 dark:text-gray-100">{label}</span> from this batch?
          </p>
        </div>
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} disabled={removing} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={removing} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ─── Pagination ─── */

function Pagination({ page, totalPages, total, pageSize, onPage }: {
  page: number; totalPages: number; total: number; pageSize: number; onPage: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800">
      <div className="text-sm text-gray-500">
        Showing <span className="font-medium text-gray-900 dark:text-gray-100">{(page - 1) * pageSize + 1}</span> to <span className="font-medium text-gray-900 dark:text-gray-100">{Math.min(page * pageSize, total)}</span> of <span className="font-medium text-gray-900 dark:text-gray-100">{total}</span>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => onPage(page - 1)} disabled={page <= 1} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <ChevronUp className="w-5 h-5 -rotate-90" />
        </button>
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">Page {page} of {totalPages}</div>
        <button onClick={() => onPage(page + 1)} disabled={page >= totalPages} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <ChevronDown className="w-5 h-5 -rotate-90" />
        </button>
      </div>
    </div>
  );
}

/* ─── Filter Dropdown (reused pattern) ─── */

function FilterChip({ label, options, selected, onChange }: {
  label: string; options: string[]; selected: string[]; onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (opt: string) => {
    const next = selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt];
    onChange(next);
  };

  if (options.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
          selected.length > 0
            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800'
            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
      >
        {label}{selected.length > 0 && ` (${selected.length})`}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 left-0 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700 dark:text-gray-300 truncate">{opt}</span>
            </label>
          ))}
          {selected.length > 0 && (
            <button onClick={() => onChange([])} className="w-full text-left px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700">
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Main Component ─── */

const CANDIDATES_PAGE_SIZE = 20;
const HISTORY_PAGE_SIZE = 20;

interface Props {
  packetId: number;
  onBack: () => void;
  onRemoveCandidate?: (packetId: number, removedCount: number) => void;
}

export function BatchDetailPage({ packetId, onBack, onRemoveCandidate }: Props) {
  const [detail, setDetail] = useState<PacketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'candidates' | 'history'>('candidates');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [removing, setRemoving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<{ ids: number[]; names: string[] } | null>(null);

  // Candidate sort/filter/page
  const [candSort, setCandSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [domainFilter, setDomainFilter] = useState<string[]>([]);
  const [activityFilter, setActivityFilter] = useState<string[]>([]);
  const [candPage, setCandPage] = useState(1);

  // History page
  const [histPage, setHistPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPacketDetail(packetId).then((d) => {
      if (!cancelled) { setDetail(d); setLoading(false); }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [packetId]);

  // Derived candidate data
  const candidates = detail?.candidates ?? [];
  const history = detail?.history ?? [];

  const domainOptions = useMemo(() => Array.from(new Set(candidates.map((c) => c.domain))).sort(), [candidates]);
  const activityOptions = useMemo(() => {
    const statuses = new Set(candidates.map((c) => c.displayStatus));
    return ['Saved', 'Viewed', 'Discarded', 'Not viewed'].filter((s) => statuses.has(s));
  }, [candidates]);

  // Filter
  let filtered = candidates;
  if (domainFilter.length > 0) filtered = filtered.filter((c) => domainFilter.includes(c.domain));
  if (activityFilter.length > 0) filtered = filtered.filter((c) => activityFilter.includes(c.displayStatus));

  // Sort
  if (candSort) {
    const { col, dir } = candSort;
    const mul = dir === 'asc' ? 1 : -1;
    filtered = [...filtered].sort((a, b) => {
      let va: string | number = '', vb: string | number = '';
      if (col === 'name') { va = (a.name || a.talentId).toLowerCase(); vb = (b.name || b.talentId).toLowerCase(); }
      else if (col === 'domain') { va = a.domain; vb = b.domain; }
      else if (col === 'activity') {
        va = ACTIVITY_PRIORITY[a.displayStatus] ?? 0;
        vb = ACTIVITY_PRIORITY[b.displayStatus] ?? 0;
      }
      if (va < vb) return -1 * mul;
      if (va > vb) return 1 * mul;
      return 0;
    });
  }

  const candTotalPages = Math.ceil(filtered.length / CANDIDATES_PAGE_SIZE);
  const pagedCandidates = filtered.slice((candPage - 1) * CANDIDATES_PAGE_SIZE, candPage * CANDIDATES_PAGE_SIZE);

  const histTotalPages = Math.ceil(history.length / HISTORY_PAGE_SIZE);
  const pagedHistory = history.slice((histPage - 1) * HISTORY_PAGE_SIZE, histPage * HISTORY_PAGE_SIZE);

  const handleCandSort = (col: string) => {
    setCandSort((prev) => prev?.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    setCandPage(1);
  };

  const confirmRemove = (candidate: PacketCandidate) => {
    setRemoveConfirm({ ids: [candidate.resultId], names: [candidate.name || candidate.talentId] });
  };

  const confirmBulkRemove = () => {
    if (selected.size === 0 || !detail) return;
    const names = detail.candidates
      .filter((c) => selected.has(c.resultId))
      .map((c) => c.name || c.talentId);
    setRemoveConfirm({ ids: Array.from(selected), names });
  };

  const executeRemove = async () => {
    if (!removeConfirm || !detail) return;
    setRemoving(true);
    try {
      await removeFromPacket(packetId, removeConfirm.ids);
      const idSet = new Set(removeConfirm.ids);
      setDetail((prev) => {
        if (!prev) return prev;
        const updated = prev.candidates.filter((c) => !idSet.has(c.resultId));
        return { ...prev, candidates: updated, candidatesCount: updated.length };
      });
      setSelected((prev) => {
        const next = new Set(prev);
        removeConfirm.ids.forEach((id) => next.delete(id));
        return next;
      });
      onRemoveCandidate?.(packetId, removeConfirm.ids.length);
      setRemoveConfirm(null);
    } catch { /* silent */ }
    finally { setRemoving(false); }
  };

  const toggleSelect = (resultId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(resultId)) next.delete(resultId); else next.add(resultId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.resultId)));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
        <span className="text-sm text-gray-500 dark:text-gray-400 mt-4">Loading batch...</span>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-8 max-w-[1400px] mx-auto">
        <button onClick={onBack} className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 transition-colors">
          <ArrowLeft className="w-4 h-4" />Back to Batches
        </button>
        <p className="text-gray-500">Batch not found.</p>
      </div>
    );
  }

  const statusBadge = (status: string) => {
    if (status === 'sent') return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Sent</span>;
    return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">Draft</span>;
  };

  const hasActiveFilters = domainFilter.length > 0 || activityFilter.length > 0;

  return (
    <div className="p-4 xl:p-8 max-w-[1400px] mx-auto space-y-6 animate-in fade-in duration-300 relative">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={onBack} className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 transition-colors">
            <ArrowLeft className="w-4 h-4" />Back to Batches
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{detail.name}</h1>
            {statusBadge(detail.status)}
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {detail.company ?? 'No company'} · {detail.candidatesCount} candidate{detail.candidatesCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</div>
            <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{formatDate(detail.createdAt)}</div>
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sent</div>
            <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{detail.sentAt ? formatDate(detail.sentAt) : '—'}</div>
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Opened</div>
            <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{detail.openedAt ? formatDate(detail.openedAt) : '—'}</div>
          </div>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created by</div>
            <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{detail.createdByName ?? '—'}</div>
          </div>
        </div>

        {/* Tabs + Content */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          {/* Tab bar */}
          <div className="border-b border-gray-200 dark:border-gray-800 px-4">
            <div className="flex items-center gap-6">
              <button
                onClick={() => setTab('candidates')}
                className={`flex items-center gap-2 py-4 text-sm font-medium border-b-2 transition-colors ${
                  tab === 'candidates'
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Users className="w-4 h-4" />
                Candidates
                <span className={`px-2 py-0.5 rounded-full text-xs ${tab === 'candidates' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                  {candidates.length}
                </span>
              </button>
              <button
                onClick={() => setTab('history')}
                className={`flex items-center gap-2 py-4 text-sm font-medium border-b-2 transition-colors ${
                  tab === 'history'
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Clock className="w-4 h-4" />
                History
                <span className={`px-2 py-0.5 rounded-full text-xs ${tab === 'history' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                  {history.length}
                </span>
              </button>
            </div>
          </div>

          {/* Candidates tab */}
          {tab === 'candidates' && (
            <>
              {/* Filters bar */}
              {(domainOptions.length > 1 || activityOptions.length > 1) && (
                <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-800 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Filter:</span>
                  {domainOptions.length > 1 && (
                    <FilterChip label="Domain" options={domainOptions} selected={domainFilter} onChange={(v) => { setDomainFilter(v); setCandPage(1); }} />
                  )}
                  {activityOptions.length > 1 && (
                    <FilterChip label="Activity" options={activityOptions} selected={activityFilter} onChange={(v) => { setActivityFilter(v); setCandPage(1); }} />
                  )}
                  {hasActiveFilters && (
                    <button onClick={() => { setDomainFilter([]); setActivityFilter([]); setCandPage(1); }} className="text-xs text-blue-600 dark:text-blue-400 hover:underline ml-1">
                      Clear all
                    </button>
                  )}
                </div>
              )}

              {/* Bulk actions bar */}
              {selected.size > 0 && (
                <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 flex items-center gap-3">
                  <span className="text-sm text-blue-700 dark:text-blue-400 font-medium">{selected.size} selected</span>
                  <button
                    onClick={confirmBulkRemove}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />Remove selected
                  </button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-600 dark:text-gray-400">
                  <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
                    <tr>
                      <th className="w-10 px-3 py-3">
                        <input
                          type="checkbox"
                          checked={filtered.length > 0 && selected.size === filtered.length}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <SortHeader label="Name" sortKey="name" currentSort={candSort} onSort={handleCandSort} />
                      <SortHeader label="Domain" sortKey="domain" currentSort={candSort} onSort={handleCandSort} />
                      <SortHeader label="Latest activity" sortKey="activity" currentSort={candSort} onSort={handleCandSort} />
                      <th className="px-3 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {pagedCandidates.map((c) => (
                      <tr key={`${c.talentId}_${c.domain}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selected.has(c.resultId)}
                            onChange={() => toggleSelect(c.resultId)}
                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-3 font-medium text-gray-900 dark:text-gray-100">
                          {c.name || c.talentId}
                        </td>
                        <td className="px-3 py-3">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{c.domain}</span>
                        </td>
                        <td className="px-3 py-3">
                          {candidateStatusBadge(c.displayStatus, c.actionAt, c.actionUsers)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <button
                            onClick={() => confirmRemove(c)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-12 text-center text-sm text-gray-400 dark:text-gray-500">
                          {hasActiveFilters ? 'No candidates match filters' : 'No candidates in this batch'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination page={candPage} totalPages={candTotalPages} total={filtered.length} pageSize={CANDIDATES_PAGE_SIZE} onPage={setCandPage} />
            </>
          )}

          {/* History tab */}
          {tab === 'history' && (
            <div className="p-6">
              {history.length === 0 ? (
                <div className="text-center py-12 text-sm text-gray-400 dark:text-gray-500">
                  No activity yet{detail.status === 'draft' ? ' — batch has not been sent' : ''}
                </div>
              ) : (
                <>
                  <div className="relative max-w-3xl">
                    <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200 dark:bg-gray-700" />
                    <div className="space-y-5">
                      {pagedHistory.map((event, i) => {
                        const config = HISTORY_ICON[event.actionType] ?? HISTORY_ICON.Viewed;
                        const Icon = config.icon;
                        return (
                          <div key={`${event.userName}-${event.at}-${i}`} className="relative flex items-start gap-4">
                            <div className={`z-10 flex h-8 w-8 items-center justify-center rounded-full border ${config.bg} ${config.text} ${config.border}`}>
                              <Icon className={`w-4 h-4 ${config.fill ? 'fill-current' : ''}`} />
                            </div>
                            <div className="min-w-0 flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3">
                              <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-sm">
                                <span className="font-semibold text-gray-900 dark:text-gray-100">{event.userName}</span>
                                <span className="text-gray-500 dark:text-gray-400">{event.actionType.toLowerCase()}</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">{event.candidateName}</span>
                              </div>
                              <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">{formatDateTime(event.at)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-4">
                    <Pagination page={histPage} totalPages={histTotalPages} total={history.length} pageSize={HISTORY_PAGE_SIZE} onPage={setHistPage} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Remove Confirmation Modal */}
      {removeConfirm && (
        <RemoveConfirmModal
          names={removeConfirm.names}
          onClose={() => { if (!removing) setRemoveConfirm(null); }}
          onConfirm={executeRemove}
          removing={removing}
        />
      )}
    </div>
  );
}
